import { Checkbox, DescriptionList } from '@doda/io-react/common';
import { ComponentProps, Dispatch, FC, SetStateAction } from 'react';
import { useAppDispatch, useAppSelector } from '@doda/pc/src/redux/hooks';
import { getJobCount } from '@doda/common/src/services/getJobCountAPI';
import classnames from 'classnames';
import { masterSelector } from '@doda/common/src/redux/features/master';
import {
  jobCountSelector,
  setAreaList,
  setIndustryLList,
  setJobCount,
  setSearchJobTypeLList,
} from '@doda/common/src/redux/features/jobCount';
import { FeatureFlagSettings } from '@doda/common/src/constants/featureFlag';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { createCheckedList } from '../../logics/createCheckedList';
import styles from './index.module.scss';
import { SearchCheckboxItem } from '../SearchCheckboxList';
import {
  CheckedList,
  DisplayItem,
  createOccupationList,
  createAreaList,
  getDisplayList,
  createIndustryList,
} from '../../logics/clickCheckbox';

/** SearchConditionListコンポーネントのプロパティ */
interface SearchConditionListProps {
  /** チェックされたリスト */
  checkedList: CheckedList;
  /** チェックされたリストの更新関数 */
  setCheckedList: Dispatch<SetStateAction<CheckedList>>;
}

/**
 * 求人条件リストの検索コンポーネント
 *
 * @param {SearchConditionListProps} props - コンポーネントのプロパティ
 * @param {CheckedList} props.checkedList - チェックされたリスト
 * @param {Dispatch<SetStateAction<CheckedList>>} props.setCheckedList - チェックされたリストの更新関数
 * @returns {JSX.Element} - SearchConditionListコンポーネント
 */
export const SearchConditionList: React.FC<SearchConditionListProps> = ({
  checkedList,
  setCheckedList,
}) => {
  const dispatch = useAppDispatch();
  const masterDataList = useAppSelector(masterSelector);
  const jobCount = useAppSelector(jobCountSelector);

  // 表示用データ取得
  const occupationList = createOccupationList(masterDataList.occupation ?? []);
  const displayOccupationList = getDisplayList(
    jobCount.searchJobTypeLList,
    checkedList.occupationCheckedList,
    occupationList
  );

  const areaList = createAreaList(masterDataList.area ?? []);
  const displayAreaList = getDisplayList(
    jobCount.areaList,
    checkedList.areaCheckedList,
    areaList
  );

  const industryList = createIndustryList(masterDataList.industry ?? []);
  const displayIndustryList = getDisplayList(
    jobCount.industryLList,
    checkedList.industryCheckedList,
    industryList
  );

  // チェック処理
  const onChangeCheckBox = (
    event: React.ChangeEvent<HTMLInputElement>,
    categorizedCheckedList: string[],
    targetStateKey: keyof CheckedList
  ) => {
    const newCheckedList = createCheckedList(
      event.target,
      categorizedCheckedList,
      targetStateKey,
      checkedList
    );
    updateCheckedList(newCheckedList);
  };

  // チェックボックス押下時状態更新処理
  const updateCheckedList = (newCheckedList: CheckedList) => {
    setCheckedList(newCheckedList);
    // 該当求人数検索
    dispatch(getNewCountList(newCheckedList));
  };

  // チェックボックス押下時求人件数取得処理
  const featureFlag = useFeatureFlag(FeatureFlagSettings.JOB_SEARCH_LIST_PC);
  const getNewCountList = (propsCheckedList: CheckedList) => () => {
    getJobCount(featureFlag, {
      areaId: propsCheckedList.areaCheckedList,
      searchJobTypeLId: propsCheckedList.occupationCheckedList,
      industryLId: propsCheckedList.industryCheckedList,
      isNewArrival: true,
    }).then((res) => {
      dispatch(setJobCount(res.totalCount ?? 0));
      dispatch(setSearchJobTypeLList(res.searchJobTypeLList ?? []));
      dispatch(setAreaList(res.areaList ?? []));
      dispatch(setIndustryLList(res.industryLList ?? []));
    });
  };

  return (
    <DescriptionList
      type="column2"
      column2dataBgColor="gray"
      className={styles.masterList}
      listData={[
        {
          title: '職種',
          contents: (
            <MasterItem
              onChange={(event) => {
                onChangeCheckBox(
                  event,
                  checkedList.occupationCheckedList,
                  'occupationCheckedList'
                );
              }}
              itemList={displayOccupationList}
            />
          ),
        },
        {
          title: 'エリア',
          contents: (
            <MasterItem
              onChange={(event) => {
                onChangeCheckBox(
                  event,
                  checkedList.areaCheckedList,
                  'areaCheckedList'
                );
              }}
              itemList={displayAreaList}
            />
          ),
        },
        {
          title: '業種',
          contents: (
            <MasterItem
              onChange={(event) => {
                onChangeCheckBox(
                  event,
                  checkedList.industryCheckedList,
                  'industryCheckedList'
                );
              }}
              itemList={displayIndustryList}
            />
          ),
        },
      ]}
    />
  );
};

/** MasterItemコンポーネントのプロパティ */
interface Props {
  /** チェックボックスのonChangeイベントハンドラ */
  onChange: ComponentProps<typeof Checkbox>['onChange'];
  /** 表示するアイテムのリスト */
  itemList: DisplayItem[];
}

/**
 * 各カテゴリの項目と求人件数を表示するコンポーネント
 *
 * @param {Props} props - コンポーネントのプロパティ
 * @param {ComponentProps<typeof Checkbox>['onChange']} props.onChange - チェックボックスのonChangeイベントハンドラ
 * @param {DisplayItem[]} props.itemList - 表示するアイテムのリスト
 * @returns {JSX.Element} - MasterItemコンポーネント
 */
const MasterItem: FC<Props> = ({ onChange, itemList }) => {
  // 折り返し要素数
  const repeatNumber = String(Math.ceil(itemList.length / 2));

  return (
    <div
      className={classnames(styles.contents__wrapper, `repeat_${repeatNumber}`)}
    >
      {itemList.map((item, i) => (
        <div className={styles.contents__child} key={i}>
          <SearchCheckboxItem
            title={item.name}
            value={item.id}
            count={item.count}
            onChange={onChange}
            isChecked={item.isChecked}
          />
        </div>
      ))}
    </div>
  );
};
